import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { actions, hooks } from 'farmx-redux-core';
import {
  Form, Button, notification, Input
} from 'antd';
import { isEqual } from 'lodash';
import { updateCustomKcValue } from '../../../helper/common';

interface AddOrEditCustomKcValueProps {
  onClick?: Function | null;
  customKcValue?: any;
  blockId: number;
}

const decimalInputRegEx = /^[0-9]*\.?[0-9]{0,2}$/;

const { loadNewBlockList } = actions;

const { useEntities } = hooks;

const AddOrEditCustomKcValue: React.FC<AddOrEditCustomKcValueProps> = ({
  onClick, customKcValue, blockId,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { activeEntityId } = useEntities();

  const [kcValue, setKcValue] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResetLoading, setIsResetLoading] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);

  // Check for valid custom kc value to show Edit and Reset options
  let isCustomKcValue: boolean = false;
  if (customKcValue && customKcValue !== 'None') isCustomKcValue = true;

  const defaultKcValue: any = isCustomKcValue ? customKcValue : kcValue;

  // To update the local state with existing custom kc value
  useEffect(() => {
    if (isCustomKcValue && !isEqual(customKcValue, kcValue)) {
      setKcValue(String(customKcValue));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customKcValue, isCustomKcValue]);

  const handleChange = (e: any) => {
    const inputValue = e?.target?.value;

    // Allow only digits and a single dot
    const isValidInput = decimalInputRegEx.test(inputValue);

    if (isValidInput) {
      setKcValue(inputValue);
    }
  };

  // To validate the input and set "save" button visibility
  const onValuesChange = (_: any, changedValues: any) => {
    const value = changedValues?.kcValue;

    // Check if the value is within range and matches the 2 decimal places rule
    const isValid = value && parseFloat(value) >= 0 && parseFloat(value) <= 2
     && decimalInputRegEx.test(value);

    setIsButtonDisabled(!isValid);
  };

  function updateKcValueOnFromSubmitOrReset(inputValue: any) {
    updateCustomKcValue(blockId, inputValue, (response: any) => {
      if (response) {
        notification.success({
          message: t('Custom Kc value updated Successfully!'),
          description: t('Changes will take an hour to reflect Kc value in calculation & graph')
        });
        // To get the latest changes from block response
        dispatch(loadNewBlockList(activeEntityId));
      } else {
        notification.error({
          message: t('Failed to update Custom Kc value'),
        });
      }
      setIsLoading(false);
      setIsResetLoading(false);
      setKcValue(null);
      setIsButtonDisabled(true);
      form.resetFields();
      if (onClick) onClick();
    });
  }

  // Reset the form fields and state when click on the "Reset" button
  function onResetClick() {
    const cropCoefficient = { custom_crop_coefficient: null };
    setIsResetLoading(true);
    updateKcValueOnFromSubmitOrReset(cropCoefficient);
  }

  // Reset the form fields when click on the "cancel" button
  function onCancelClick() {
    form.resetFields();
    if (onClick) onClick();
  }

  // Call API and add/edit custom Kc value on form submit
  function onFormSubmit(data: any) {
    const { kcValue } = data || {};

    // To maintain the decimal value format
    let inputValue = kcValue;
    if (inputValue.endsWith('.')) {
      inputValue += '0';
    }
    const cropCoefficient = { custom_crop_coefficient: inputValue };
    setIsLoading(true);
    updateKcValueOnFromSubmitOrReset(cropCoefficient);
  }

  useEffect(() => {
    // Update form values when defaultKcValue changes
    form.setFieldsValue({ kcValue: defaultKcValue });
  }, [defaultKcValue, form]);

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={{ kcValue: defaultKcValue }}
      onValuesChange={onValuesChange}
      onFinish={onFormSubmit}
      scrollToFirstError
    >
      <div className="margin-bottom-10 flex-row justify-content-space-between">
        <Form.Item
          name="kcValue"
          className="form-margin form-button-width"
          rules={[
            {
              validator: (_, value) => {
                const decimalRegex = /^(?:[0-1](?:\.\d{0,2})?|2(?:\.0{0,2})?)$/;
                if (!value || decimalRegex.test(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(
                  'Please enter a value between 0.0 and 2.0, with up to 2 decimal places'
                ));
              },
            },
          ]}
        >
          <Input
            value={kcValue}
            onChange={handleChange}
            placeholder={t('Enter a value from 0.0 to 2.0')}
            maxLength={4}
          />
        </Form.Item>
      </div>
      <div className="flex-row">
        <Form.Item className="form-margin form-button-margin form-button-width">
          <Button type="ghost" onClick={onCancelClick} block>
            {t('Cancel')}
          </Button>
        </Form.Item>
        <Form.Item className="form-margin form-button-margin
         form-button-width form-button-right-align"
        >
          <Button
            type={isButtonDisabled ? 'default' : 'primary'}
            htmlType="submit"
            loading={isLoading}
            disabled={isButtonDisabled}
            block
          >
            {t('Save')}
          </Button>
        </Form.Item>
        {isCustomKcValue && (
        <Form.Item className="form-margin form-button-margin
         form-button-width form-button-right-align"
        >
          <Button
            type="primary"
            loading={isResetLoading}
            onClick={onResetClick}
            block
          >
            {t('Reset')}
          </Button>
        </Form.Item>
        )}
      </div>
    </Form>
  );
};

export default AddOrEditCustomKcValue;
